import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Search, Download, Upload, Pencil, Trash2, Tag } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const CATEGORIES = ['excavation','steel','plumbing','electrical','gas','gunite','tile','plaster','decking','equipment','utilities','covers','features','permits','misc'];
const UNITS = ['EA','LF','SF','CY','HR','LOT'];

const EMPTY_ITEM = { category: 'misc', name: '', unit: 'LOT', cost: 0, default_markup_pct: 33, default_sell_price: '', is_allowance: false, allowance_amount: '', notes: '' };

export default function PricingLibrary() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_ITEM);

  const load = useCallback(async () => {
    const params = {};
    if (catFilter && catFilter !== 'all') params.category = catFilter;
    if (search) params.search = search;
    const r = await api.get('/pricing', { params });
    setItems(r.data);
  }, [catFilter, search]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    const payload = { ...form, cost: parseFloat(form.cost) || 0, default_markup_pct: parseFloat(form.default_markup_pct) || 33,
      default_sell_price: form.default_sell_price ? parseFloat(form.default_sell_price) : null,
      is_allowance: form.is_allowance || false,
      allowance_amount: form.allowance_amount ? parseFloat(form.allowance_amount) : null };
    if (editItem) {
      await api.put(`/pricing/${editItem.id}`, payload);
      toast.success('Item updated');
    } else {
      await api.post('/pricing', payload);
      toast.success('Item created');
    }
    setDialogOpen(false);
    setEditItem(null);
    setForm(EMPTY_ITEM);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pricing item?')) return;
    await api.delete(`/pricing/${id}`);
    toast.success('Deleted');
    load();
  };

  const handleExport = async () => {
    const r = await api.get('/pricing/export-csv', { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a'); a.href = url; a.download = 'pricing_library.csv'; a.click();
    toast.success('CSV exported');
  };

  const handleImport = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    const r = await api.post('/pricing/import-csv', fd);
    toast.success(`Imported ${r.data.imported} items`);
    load();
  };

  const openEdit = (item) => {
    setEditItem(item);
    const sell = item.default_sell_price || Math.round(item.cost * (1 + item.default_markup_pct / 100));
    setForm({ category: item.category, name: item.name, unit: item.unit, cost: item.cost,
      default_markup_pct: item.default_markup_pct, default_sell_price: sell,
      is_allowance: item.is_allowance || false, allowance_amount: item.allowance_amount || '',
      notes: item.notes || '' });
    setDialogOpen(true);
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="pricing-library-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Pricing Library</h1>
          <p className="text-slate-500 text-sm mt-1">{items.length} items</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
          <label>
            <Button variant="outline" size="sm" asChild data-testid="import-csv-btn">
              <span><Upload className="w-4 h-4 mr-1" /> Import CSV</span>
            </Button>
            <input type="file" accept=".csv" className="hidden" onChange={handleImport} />
          </label>
          <Button size="sm" className="bg-sky-500 hover:bg-sky-600 text-white" onClick={() => { setEditItem(null); setForm(EMPTY_ITEM); setDialogOpen(true); }} data-testid="add-pricing-item-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Item
          </Button>
        </div>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search items..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10"
            data-testid="pricing-search-input"
          />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-44 h-10" data-testid="pricing-category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-100">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold text-slate-700">Category</TableHead>
              <TableHead className="font-semibold text-slate-700">Name</TableHead>
              <TableHead className="font-semibold text-slate-700">Unit</TableHead>
              <TableHead className="font-semibold text-slate-700 text-right">Cost</TableHead>
              <TableHead className="font-semibold text-slate-700 text-right">Markup %</TableHead>
              <TableHead className="font-semibold text-slate-700 text-right">Sell Price</TableHead>
              <TableHead className="font-semibold text-slate-700">Notes</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => {
              const sell = item.default_sell_price || Math.round(item.cost * (1 + item.default_markup_pct / 100));
              const displayMarkup = item.default_sell_price && item.cost > 0
                ? Math.round(((item.default_sell_price - item.cost) / item.cost) * 100)
                : item.default_markup_pct;
              return (
                <TableRow key={item.id} className="hover:bg-sky-50/50 transition-colors" data-testid={`pricing-row-${item.id}`}>
                  <TableCell>
                    <Badge variant="outline" className="capitalize text-xs">{item.category}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-slate-800">{item.name}</span>
                      {item.is_allowance && <Badge className="bg-amber-100 text-amber-700 text-[9px] px-1 py-0">ALLOW</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-500 font-mono text-xs">{item.unit}</TableCell>
                  <TableCell className="text-right font-mono text-sm">${item.cost.toLocaleString()}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{displayMarkup}%</TableCell>
                  <TableCell className="text-right font-mono text-sm font-semibold text-slate-800">${sell.toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-slate-400 max-w-[200px] truncate">{item.notes}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)} data-testid={`edit-pricing-${item.id}`}>
                        <Pencil className="w-3.5 h-3.5 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(item.id)} data-testid={`delete-pricing-${item.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="pricing-item-dialog">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit Pricing Item' : 'Add Pricing Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm(p => ({...p, category: v}))}>
                  <SelectTrigger data-testid="pricing-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unit</Label>
                <Select value={form.unit} onValueChange={v => setForm(p => ({...p, unit: v}))}>
                  <SelectTrigger data-testid="pricing-form-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Name</Label><Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} data-testid="pricing-form-name" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Cost ($)</Label><Input type="number" step="0.01" value={form.cost}
                onChange={e => setForm(p => ({...p, cost: e.target.value}))}
                onBlur={e => {
                  const cost = parseFloat(e.target.value) || 0;
                  const markup = parseFloat(form.default_markup_pct) || 0;
                  const sell = Math.round(cost * (1 + markup / 100));
                  setForm(p => ({...p, cost, default_sell_price: sell || ''}));
                }}
                data-testid="pricing-form-cost" /></div>
              <div><Label>Markup %</Label><Input type="number" value={form.default_markup_pct}
                onChange={e => setForm(p => ({...p, default_markup_pct: e.target.value}))}
                onBlur={e => {
                  const markup = parseFloat(e.target.value) || 0;
                  const cost = parseFloat(form.cost) || 0;
                  const sell = Math.round(cost * (1 + markup / 100));
                  setForm(p => ({...p, default_markup_pct: markup, default_sell_price: sell || ''}));
                }}
                data-testid="pricing-form-markup" /></div>
              <div><Label>Sell Price</Label><Input type="number" step="0.01" value={form.default_sell_price}
                onChange={e => setForm(p => ({...p, default_sell_price: e.target.value}))}
                onBlur={e => {
                  const sell = parseFloat(e.target.value) || 0;
                  const cost = parseFloat(form.cost) || 0;
                  if (cost > 0 && sell > 0) {
                    const markup = Math.round(((sell - cost) / cost) * 100);
                    setForm(p => ({...p, default_sell_price: sell, default_markup_pct: markup}));
                  }
                }}
                placeholder="Auto" data-testid="pricing-form-sell-price" /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2} data-testid="pricing-form-notes" /></div>
            <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <Switch checked={form.is_allowance} onCheckedChange={v => setForm(p => ({...p, is_allowance: v}))} data-testid="pricing-form-allowance-toggle" />
              <div className="flex-1">
                <Label className="text-amber-800 font-semibold">Allowance Item</Label>
                <p className="text-[10px] text-amber-700">Budget cap for customer selections. Upgrades beyond = change order.</p>
              </div>
            </div>
            {form.is_allowance && (
              <div>
                <Label>Allowance Amount ($)</Label>
                <Input type="number" step="100" value={form.allowance_amount} onChange={e => setForm(p => ({...p, allowance_amount: e.target.value}))}
                  placeholder="Same as sell price" data-testid="pricing-form-allowance-amount" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-sky-500 hover:bg-sky-600 text-white" onClick={handleSave} data-testid="pricing-form-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
